// 1-D Dynamic Programming — full course content for the DSA Explanation Modal.
// Java-focused. Same section-course schema as backtracking.js: concept → internals →
// applied templates → errors → complexity → beginner drills → cheatsheet → playground.

import dp1dDrills from './dp1dDrills';
const drill = id => dp1dDrills.find(d => d.id === id);

const dp1d = {
  key: '1-D Dynamic Programming',
  tagline: 'Break a problem into a sequence of overlapping subproblems, solve each exactly once, and reuse the answer forever after.',

  sections: [
    {
      id: 'intro',
      title: 'Introduction to 1-D Dynamic Programming',
      type: 'text',
      body: [
        "1-D Dynamic Programming applies when a problem's answer can be described by a SINGLE moving index — a position in an array, a step on a staircase, a remaining amount of money — and the answer at each point can be built entirely from a small, fixed number of earlier answers along that same line.",
        "The \"1-D\" refers to the shape of the table of subproblems: a plain array, dp[0], dp[1], dp[2], …, dp[n], rather than a grid. Once dp[i] is computed, it's stored and never recomputed — every later index that needs it simply reads it back, instead of re-deriving it from scratch.",
      ],
    },
    {
      id: 'why',
      title: 'Why 1-D Dynamic Programming?',
      type: 'text',
      body: [
        "Plenty of problems have a perfectly correct recursive solution already — but written naively, that recursion often asks the exact same question (\"what's the best result starting from index i?\") over and over, through many different call paths. Climbing Stairs, House Robber, and Coin Change all have this shape: the SAME smaller subproblem recurs constantly, and naive recursion re-solves it every single time, turning what should be a fast linear-time answer into an exponential one.",
        "Dynamic programming is simply the discipline of noticing that repetition and refusing to pay for it twice: solve each distinct subproblem once, remember the answer (a table, top-down via memoization, or bottom-up via direct tabulation), and every future reference to that subproblem becomes an O(1) lookup instead of a fresh computation. The same exponential recursion collapses to linear or near-linear time — not by changing the LOGIC, only by refusing to forget.",
      ],
    },
    {
      id: 'analogy',
      title: 'How 1-D DP Works — Two Real-World Pictures',
      type: 'text',
      heading: 'A Staircase Poured One Step at a Time, and a Hiker\'s Notepad',
      body: [
        "Picture a construction crew pouring a concrete staircase, one step at a time, from the bottom up. Each step's exact height only depends on the ONE or TWO steps poured immediately before it — never on redoing measurements from scratch, and never on revisiting a step once it's set. By the time the crew reaches the top step, every step below it is finished, fixed, and available to read — that fixed, bottom-up table of finished steps IS the dp array.",
        "Now picture a hiker walking a long trail with checkpoints, carrying a small notepad. The first time they work out \"the fastest way to reach checkpoint 7,\" they jot the answer down next to the number 7. Later, if that same question comes up again — because checkpoint 12's fastest route happens to pass back through checkpoint 7 — they don't re-walk the trail to re-derive it. They just flip to page 7 and read the answer. That notepad, indexed by checkpoint number, is exactly what a dp array (or a memoization cache) does for a recursive solution.",
      ],
    },
    {
      id: 'visualize',
      title: 'Visualizing the DP Table',
      type: 'dp-table-diagram',
      body: [
        "Every 1-D DP solution eventually fills in a table like this one — the Fibonacci-shaped recurrence behind Climbing Stairs. Each entry is computed exactly once, directly from the ONE or TWO entries immediately before it, and is never touched again once set.",
        "dp[5] here reads dp[4] and dp[3] — not the whole table, not every earlier entry, just a small fixed window. That's the defining shape of a 1-D DP transition.",
      ],
      demo: {
        values: [0, 1, 1, 2, 3, 5],
        labelPrefix: 'dp',
        highlightIndices: [5, 4, 3],
        note: 'dp[5] = dp[4] + dp[3] = 3 + 2 = 5 — built from exactly two earlier, already-finished entries.',
      },
    },
    {
      id: 'template',
      title: 'The Universal 1-D DP Recipe',
      type: 'code',
      code:
`int solve(/* state — usually a single index i */) {
    if (/* base case: the smallest i (or two) with a known answer */) {
        return /* that known answer directly, no recursion needed */;
    }
    // dp[i] must be built from a FIXED, SMALL set of earlier states —
    // typically dp[i-1], sometimes also dp[i-2]. Never "rescan everything before i"
    // unless the problem's rule genuinely requires that (e.g. Longest Increasing Subsequence).
    return /* combine those earlier states according to the problem's specific rule */;
}`,
      note: 'Every problem in this pattern follows five steps: (1) define what dp[i] MEANS in one sentence, (2) identify the base case(s), (3) write the transition — how dp[i] follows from earlier entries, (4) fill the table in an order where every dependency is already computed, (5) read off the answer — usually dp[n], sometimes the max/min/sum across the whole table.',
    },
    {
      id: 'climbtemplate',
      title: 'Applied: The Fibonacci-Shaped Recurrence',
      type: 'code',
      code:
`int climbStairs(int n) {
    if (n <= 2) return n;                     // base case
    int[] dp = new int[n + 1];
    dp[1] = 1; dp[2] = 2;
    for (int i = 3; i <= n; i++) {
        dp[i] = dp[i - 1] + dp[i - 2];         // transition: today = yesterday + the day before
    }
    return dp[n];                              // answer
}`,
      note: 'dp[i] here means "the number of ways to reach step i." This "today depends on the last two entries, added together" shape is the single most common 1-D DP recurrence — it also drives Decode Ways and (with a max instead of a sum) House Robber.',
    },
    {
      id: 'robbertemplate',
      title: 'Applied: The Skip-or-Take Recurrence',
      type: 'code',
      code:
`int rob(int[] nums) {
    int[] dp = new int[nums.length];
    dp[0] = nums[0];
    dp[1] = Math.max(nums[0], nums[1]);
    for (int i = 2; i < nums.length; i++) {
        dp[i] = Math.max(dp[i - 1], dp[i - 2] + nums[i]);   // skip i, vs. take i
    }
    return dp[dp.length - 1];
}`,
      note: 'dp[i] here means "the best result achievable using only the first i+1 elements." The "compare skipping this element against taking it (plus whatever was best two steps back)" shape reappears directly in House Robber II, Delete and Earn, and — in boolean form — Partition Equal Subset Sum.',
    },
    {
      id: 'topdownvsbottomup',
      title: 'Top-Down (Memoization) vs. Bottom-Up (Tabulation)',
      type: 'code',
      code:
`// Top-down: still written as ordinary recursion, but a cache remembers
// each answer the FIRST time it's computed — every later request for the
// same n is then an instant lookup instead of a fresh recursive call.
int climbStairsMemo(int n, int[] memo) {
    if (n <= 2) return n;
    if (memo[n] != 0) return memo[n];                  // already solved — reuse it
    memo[n] = climbStairsMemo(n - 1, memo) + climbStairsMemo(n - 2, memo);
    return memo[n];
}`,
      note: 'Top-down and bottom-up compute the exact same table — they only differ in direction. Top-down starts from the question ("what\'s the answer for n?") and works backward, caching along the way; bottom-up starts from the base cases and works forward, filling every entry before it\'s ever asked for. Bottom-up is usually preferred in interviews (no recursion overhead, no stack-depth risk on large inputs), but deriving the top-down version first — then mechanically converting it — is often the easiest path to get there.',
    },
    {
      id: 'errors',
      title: 'Common Errors',
      type: 'errors',
      items: [
        {
          mistake: 'Mixing up the loop direction for reuse rules',
          why: 'Scanning the target amount forward (low → high) allows an item to be reused any number of times (Coin Change); scanning backward (high → low) forbids reuse (Partition Equal Subset Sum). The two loops look almost identical in code but silently produce a completely different problem if swapped.',
          fix: 'Ask explicitly: can this item be reused? If yes, scan forward. If each item is used at most once, scan backward.',
        },
        {
          mistake: 'Using 0 as the "unreachable" sentinel for a minimization problem',
          why: 'For problems like Coin Change, leaving an unreached dp[i] at 0 makes it look like "zero coins needed" instead of "impossible" — silently corrupting every later Math.min() that reads it.',
          fix: 'For minimization, initialize unreached states to a clearly-impossible large sentinel (Integer.MAX_VALUE) and check for it before combining. For maximization, use a clearly-impossible small sentinel — never a value that could also be a legitimate answer.',
        },
        {
          mistake: 'Off-by-one errors in the dp array\'s size or starting index',
          why: 'Many 1-D DP problems need dp[0] to represent a real, meaningful base case — an empty prefix, a zero amount. Sizing the array to n instead of n + 1, or starting the fill loop at the wrong index, silently drops or duplicates a state.',
          fix: 'Before coding, write out in one sentence exactly what dp[0] and dp[n] each MEAN for this specific problem, and size and loop the array to match that meaning precisely.',
        },
        {
          mistake: 'Adding a memo array to top-down recursion, but forgetting the cache check',
          why: 'A memoized-looking function that never checks "is this already solved?" before recursing — or never stores the result before returning — LOOKS optimized while still silently recomputing every subproblem from scratch, staying exponential.',
          fix: 'Every memoized function needs exactly two extra lines versus the naive version: an early check-and-return-if-cached at the top, and storing the result in the cache immediately before it\'s returned.',
        },
      ],
    },
    {
      id: 'complexity',
      title: 'Time Complexity',
      type: 'complexity',
      rows: [
        { op: 'Fibonacci-shaped (Climbing Stairs, Decode Ways)', time: 'O(n)', note: 'Each dp[i] depends on exactly the 1–2 entries before it — a single forward pass.' },
        { op: 'Skip-or-take (House Robber family)', time: 'O(n)', note: 'Same single-pass shape, using max() instead of addition.' },
        { op: 'Unbounded target-fill (Coin Change, Perfect Squares)', time: 'O(target × items)', note: 'Every amount from 1 to target tries every item — reuse is allowed.' },
        { op: '0/1 knapsack-shaped (Partition Equal Subset Sum)', time: 'O(n × target)', note: 'Each item is considered against every possible sum, but used at most once per pass.' },
        { op: 'Longest Increasing Subsequence (O(n²) version)', time: 'O(n²)', note: 'Each dp[i] scans every earlier index — optimizable to O(n log n) with binary search.' },
      ],
    },
    {
      id: 'when',
      title: 'When to Use',
      type: 'text',
      heading: 'Reach for 1-D DP when you see…',
      body: [
        'A request for an optimal NUMBER — a minimum cost, a maximum value, a count of ways, or a yes/no feasibility — not a request for every possible way (that\'s Backtracking\'s job).',
        'A brute-force recursive solution that visibly re-solves the exact same smaller subproblem multiple times through different call paths (overlapping subproblems).',
        'A state that collapses to a SINGLE number — an index, a remaining amount, a prefix length — rather than a whole path, set, or combination of choices.',
        'Optimal substructure: the best answer at position i can be built entirely from the best answers at a few earlier positions, without needing to know exactly HOW those earlier answers were achieved.',
        'Consider a different tool (or a second dimension — 2-D DP) when the state genuinely needs two independent numbers to describe it, or when there\'s no real overlap between subproblems at all.',
      ],
    },
    {
      id: 'problems',
      title: 'Beginner Problems (Easy → Medium → Advanced)',
      type: 'problems',
      groups: [
        {
          level: 'Easy',
          items: [
            drill('fibonacci-number'),
            drill('climbing-stairs-drill'),
            drill('min-cost-climbing-stairs'),
          ],
        },
        {
          level: 'Medium',
          items: [
            drill('house-robber-drill'),
            drill('delete-and-earn'),
            drill('perfect-squares'),
          ],
        },
        {
          level: 'Advanced',
          items: [
            drill('coin-change-drill'),
            drill('longest-increasing-subsequence-drill'),
            drill('combination-sum-iv'),
          ],
        },
      ],
      note: 'These are foundation drills to build comfort with defining dp[i], its base case, and its transition before moving into the curated interview problem list for this pattern.',
    },
    {
      id: 'cheatsheet',
      title: 'Cheatsheet & Recap',
      type: 'cheatsheet',
      items: [
        'Five-step recipe: define what dp[i] means, find the base case(s), write the transition, fill in a valid order, read off the answer.',
        'Fibonacci-shape (dp[i] = dp[i-1] + dp[i-2]) powers Climbing Stairs and Decode Ways; the skip-or-take shape (max of skip vs. take-two-back) powers the House Robber family.',
        'Minimization problems need an "impossible" sentinel (like Integer.MAX_VALUE) — never 0 — for unreached states.',
        'Reuse allowed (Coin Change, Perfect Squares): scan the target forward. Reuse forbidden (Partition Equal Subset Sum): scan the target backward.',
        'Top-down (memoized recursion) and bottom-up (tabulation) compute the identical table — bottom-up is usually preferred in interviews for its lack of recursion overhead and stack-depth risk.',
        'The final answer isn\'t always dp[n-1] — for Longest Increasing Subsequence, it\'s the MAXIMUM value across the entire table, since the best subsequence might end anywhere.',
        'If the state needs two independent numbers to describe (not just one moving index), that\'s a signal to look at 2-D Dynamic Programming instead.',
      ],
    },
    {
      id: 'playground',
      title: 'Interactive Practice & Quiz',
      type: 'playground',
    },
  ],
};

export default dp1d;
